import * as React from 'react'
import { Slot } from 'primitives/slot'
import { focusRing } from '@repo/studio-design-system/utilities'
import { tw } from 'utils/classnames'

export type CardVariant = 'non-interactive' | 'interactive'
export type CardPadding = 'none' | 'micro' | 'tight' | 'compact' | 'standard' | 'spacious'
export type CardRadius = 'none' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'

const paddingClasses: Record<CardPadding, string> = {
  none: 'p-[var(--studio-geometry-semantic-space-card-padding-none)]',
  micro: 'p-[var(--studio-geometry-semantic-space-card-padding-micro)]',
  tight: 'p-[var(--studio-geometry-semantic-space-card-padding-tight)]',
  compact: 'p-[var(--studio-geometry-semantic-space-card-padding-compact)]',
  standard: 'p-[var(--studio-geometry-semantic-space-card-padding-standard)]',
  spacious: 'p-[var(--studio-geometry-semantic-space-card-padding-spacious)]',
}

const radiusClasses: Record<CardRadius, string> = {
  none: 'rounded-studio-geometry-semantic-radius-none',
  '2xs': 'rounded-studio-geometry-semantic-radius-2xs',
  xs: 'rounded-studio-geometry-semantic-radius-xs',
  sm: 'rounded-studio-geometry-semantic-radius-sm',
  md: 'rounded-studio-geometry-semantic-radius-md',
  lg: 'rounded-studio-geometry-semantic-radius-lg',
  xl: 'rounded-studio-geometry-semantic-radius-xl',
  '2xl': 'rounded-studio-geometry-semantic-radius-2xl',
  full: 'rounded-studio-geometry-semantic-radius-full',
}

interface CardSharedProps {
  padding?: CardPadding
  radius?: CardRadius
}

export type NonInteractiveCardProps = Omit<React.ComponentProps<'div'>, 'ref'> &
  CardSharedProps & {
    variant?: 'non-interactive'
    ref?: React.Ref<HTMLDivElement>
  }

export type InteractiveCardProps = Omit<React.ComponentProps<'button'>, 'ref'> &
  CardSharedProps & {
    variant: 'interactive'
    /**
     * Renders the selected treatment and exposes `aria-pressed`. Selection is owned by the
     * consumer. Omit it entirely for cards that are clickable but not selectable. A card rendered
     * as a `RadioGroup.Item` gets `aria-checked` from the group instead.
     */
    selected?: boolean
    /**
     * Renders the child element instead of a button, for cards whose whole surface navigates. The
     * child owns its own semantics, so no button `type` is applied.
     */
    asChild?: boolean
    ref?: React.Ref<HTMLButtonElement>
  }

export type CardProps = NonInteractiveCardProps | InteractiveCardProps

/**
 * Card — a single-slot surface container for grouping related content.
 *
 * `non-interactive` is a static container with no state changes. `interactive` renders a button
 * that responds to hover, active, focus and disabled states, and can be marked as selected.
 *
 * Do not use for compact dropdown items, dense table cells or single-line settings lists.
 *
 * @figma https://www.figma.com/design/mKWuIdPpAnPyyQnGc1pbY5/Studio-DS---Components?node-id=1777-12639
 */
export function Card(props: NonInteractiveCardProps): React.ReactElement
export function Card(props: InteractiveCardProps): React.ReactElement
export function Card({
  variant = 'non-interactive',
  padding = 'standard',
  radius = 'xl',
  className,
  ...props
}: CardProps) {
  const sharedClassName = tw(
    'flex w-full flex-col items-start border border-solid overflow-clip',
    paddingClasses[padding],
    radiusClasses[radius],
  )

  if (variant === 'interactive') {
    const { selected, asChild, type, ...interactiveProps } = props as Omit<
      InteractiveCardProps,
      'variant' | 'padding' | 'radius' | 'className'
    >
    const Component = asChild ? Slot : 'button'

    return (
      <Component
        aria-pressed={interactiveProps.role === 'radio' ? undefined : selected}
        type={asChild ? undefined : (type ?? 'button')}
        {...interactiveProps}
        className={tw(
          sharedClassName,
          'text-left outline-none transition-colors',
          focusRing,
          selected ? interactiveClasses.selected : interactiveClasses.unselected,
          interactiveProps.disabled && 'cursor-not-allowed',
          className,
        )}
      />
    )
  }

  return (
    <div
      {...(props as Omit<NonInteractiveCardProps, 'variant' | 'padding' | 'radius' | 'className'>)}
      className={tw(
        sharedClassName,
        'border-theme_semantic_surface_border_color-subtle bg-theme_semantic_surface_container_subtle',
        className,
      )}
    />
  )
}

const interactiveClasses = {
  unselected: [
    'border-theme_semantic_control_selection_container_unselected_border_color-default bg-theme_semantic_control_selection_container_unselected_background_color-default',
    'hover:border-theme_semantic_control_selection_container_unselected_border_color-hover hover:bg-theme_semantic_control_selection_container_unselected_background_color-hover',
    'active:border-theme_semantic_control_selection_container_unselected_border_color-active active:bg-theme_semantic_control_selection_container_unselected_background_color-active',
    'focus-visible:border-theme_semantic_control_selection_container_unselected_border_color-focus focus-visible:bg-theme_semantic_control_selection_container_unselected_background_color-focus',
    'disabled:border-theme_semantic_control_selection_container_unselected_border_color-disabled disabled:bg-theme_semantic_control_selection_container_unselected_background_color-disabled',
  ].join(' '),
  selected: [
    'border-theme_semantic_control_selection_container_selected_border_color-default bg-theme_semantic_control_selection_container_selected_background_color-default',
    'hover:border-theme_semantic_control_selection_container_selected_border_color-hover hover:bg-theme_semantic_control_selection_container_selected_background_color-hover',
    'active:border-theme_semantic_control_selection_container_selected_border_color-active active:bg-theme_semantic_control_selection_container_selected_background_color-active',
    'focus-visible:border-theme_semantic_control_selection_container_selected_border_color-focus focus-visible:bg-theme_semantic_control_selection_container_selected_background_color-focus',
    'disabled:border-theme_semantic_control_selection_container_selected_border_color-disabled disabled:bg-theme_semantic_control_selection_container_selected_background_color-disabled',
  ].join(' '),
}
